import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { UserPlus, Search, Trash2, GraduationCap, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InscriptionsPage = () => {
  const [inscriptions, setInscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API}/inscriptions`, { credentials: 'include' });
      if (res.ok) setInscriptions(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette inscription ?')) return;
    await fetch(`${API}/inscriptions/${id}`, { method: 'DELETE', credentials: 'include' });
    toast.success('Inscription supprimée');
    fetchData();
  };

  const filtered = inscriptions.filter(i =>
    i.learner_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    i.course_title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="inscriptions-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Inscriptions</h1>
            <p className="text-slate-500">Gérez les inscriptions des apprenants aux formations</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Card><CardContent className="pt-6"><p className="text-sm text-slate-500">Total inscriptions</p><p className="text-2xl font-bold text-slate-900">{inscriptions.length}</p></CardContent></Card>
          <Card><CardContent className="pt-6"><p className="text-sm text-slate-500">Actives</p><p className="text-2xl font-bold text-teal-600">{inscriptions.filter(i => i.status === 'active').length}</p></CardContent></Card>
          <Card><CardContent className="pt-6"><p className="text-sm text-slate-500">Complétées</p><p className="text-2xl font-bold text-emerald-600">{inscriptions.filter(i => i.status === 'completed').length}</p></CardContent></Card>
        </div>

        <Card>
          <CardHeader>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Rechercher par apprenant ou formation..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" />
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Apprenant</TableHead>
                  <TableHead>Formation</TableHead>
                  <TableHead>Date d'inscription</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(insc => (
                  <TableRow key={insc.enrollment_id} data-testid={`inscription-row-${insc.enrollment_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                          <UserPlus className="h-5 w-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{insc.learner_name}</p>
                          <p className="text-sm text-slate-500">{insc.learner_email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-teal-500" />
                        <span className="font-medium">{insc.course_title}</span>
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-sm text-slate-600">
                        <Calendar className="h-3 w-3" />
                        {new Date(insc.enrolled_at).toLocaleDateString('fr-FR')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        insc.status === 'active' ? 'bg-teal-100 text-teal-700' :
                        insc.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-slate-100 text-slate-600'
                      }>
                        {insc.status === 'active' ? 'Active' : insc.status === 'completed' ? 'Complétée' : 'Abandonnée'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(insc.enrollment_id)}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Aucune inscription trouvée</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default InscriptionsPage;
